import { useTranslation } from '@mukil/i18n';
import { Button, EmptyState, GlassCard, StatTile } from '@jumpstone/ui-react';
import { ScreenHeader } from '../../components/common/screen_header';
import { StatutoryDeadlineCard } from './statutory_deadline_card';
import { ActionNeededCard } from './action_needed_card';
import { ActivityFeedCard } from './activity_feed_card';
import { CurrentRunCard } from './current_run_card';
import { ExpiringDocumentsCard } from './expiring_documents_card';
import { useDashboard } from '../../data/use_dashboard';
import type { ApprovalsTileStatus } from '../../data/use_dashboard';
import { usePayRuns } from '../../data/use_pay_runs';
import {
  deriveComplianceCaption,
  greetingPartForHour,
  type ComplianceCounts,
} from '../../data/wagepro_store_dashboard';
import {
  format_month_year,
  format_money_compact,
  format_number,
  format_signed_basis_points,
  format_signed_number,
} from '../../lib/format';
import { navigate, type Route } from '../../lib/router';

/**
 * Admin landing screen: the cycle at a glance plus everything awaiting
 * action.
 *
 * `useDashboard()`'s `state` composes FOUR independent real sources (C4,
 * #22328 — see that hook's own module docblock) behind one shared entity
 * context. `loading`/`no_entity` render the same full-screen skeleton
 * `payroll_history_screen.tsx` (C3) uses for its own equivalent states —
 * `no_entity` is practically unreachable (see `use_dashboard.ts`'s
 * docblock), same stance that screen takes. Once `ready`, each KPI tile
 * renders its OWN `tile_statuses` entry: a tile whose underlying source
 * failed shows an explicit unavailable/error state, never `summary`'s
 * mock-backed fallback number for that field — partial degradation is
 * real (one source can fail independently of the other three) and must
 * never collapse to a single global error screen.
 */
export function DashboardScreen(): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const {
    state,
    pending_approvals,
    pending_approvals_status,
    pending_approvals_route,
    retry,
  } = useDashboard();
  // `usePayRuns()`'s `state` is only `ready` once the real OAuth fetch
  // resolves (C3, #22327) -- `loading`/`entitlement_required`/`error` (and
  // the practically-unreachable `no_entity`) all treat this card as "no
  // current run to show" rather than surfacing a second, separate error UI
  // on top of the dashboard's own `useDashboard()` state -- the current-run
  // card is a convenience shortcut to the full history/wizard screens,
  // which already surface their own load-error/retry affordance.
  const { state: pay_runs_state } = usePayRuns();
  const current_run =
    pay_runs_state.status === 'ready' ? pay_runs_state.current_run : null;

  if (state.status === 'loading' || state.status === 'no_entity') {
    return (
      <>
        <ScreenHeader title={t('dashboard.greeting_loading')} />
        <GlassCard variant="accent" className="rounded-2xl p-5" role="status">
          <span className="sr-only">{t('dashboard.loading')}</span>
          <div className="nv-skel" style={{ height: 44, margin: 8 }} />
          <div className="nv-skel" style={{ height: 44, margin: 8 }} />
          <div className="nv-skel" style={{ height: 44, margin: 8 }} />
        </GlassCard>
      </>
    );
  }

  const { summary, tile_statuses, action_items, compliance_counts } = state;
  const greeting_part = greetingPartForHour(new Date().getHours());
  // Discriminant → literal `t()` switch (static i18n-extract compatible,
  // see `packages/config-eslint/no-dynamic-i18n-keys.js`'s migration
  // pattern). Inlined here rather than pushed into a helper because the
  // extractor's scoped-namespace resolution only sees `t()` inside a
  // function that has `useTranslation('wp')` in scope; a helper taking
  // `t` as a parameter and calling `t('dashboard.greeting_morning')`
  // would be missed (3-segment keys aren't recognised in the inline
  // fallback).
  let greeting_title: string;
  switch (greeting_part) {
    case 'morning':
      greeting_title = t('dashboard.greeting_morning', {
        name: summary.greeting_name,
      });
      break;
    case 'afternoon':
      greeting_title = t('dashboard.greeting_afternoon', {
        name: summary.greeting_name,
      });
      break;
    case 'evening':
      greeting_title = t('dashboard.greeting_evening', {
        name: summary.greeting_name,
      });
      break;
  }

  return (
    <>
      <ScreenHeader
        title={greeting_title}
        description={t('dashboard.subtitle', {
          company: summary.company_name,
          period: format_month_year(summary.period, locale),
        })}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                navigate({ module: 'employees', view: 'directory' });
              }}
            >
              {t('employees.add')}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                navigate({ module: 'payroll', tab: 'run' });
              }}
            >
              {t('pay_run.run_payroll')}
            </Button>
          </>
        }
      />

      <div className="grid gap-3 mb-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
        {tile_statuses.headcount === 'ready' ? (
          <StatTile
            kicker={t('dashboard.stat.headcount')}
            value={format_number(summary.headcount, locale)}
            delta={t('dashboard.stat.headcount_delta', {
              delta: format_signed_number(summary.headcount_delta, locale),
            })}
            deltaIntent={summary.headcount_delta >= 0 ? 'positive' : 'negative'}
          />
        ) : (
          <StatTile
            kicker={t('dashboard.stat.headcount')}
            value="—"
            delta={t('dashboard.tile_unavailable')}
            // Neutral, not 'negative' -- 'negative' is the same danger-red
            // used for a genuinely bad KPI value (e.g. a payroll decrease).
            // An unavailable tile is a load failure, not a bad number, and
            // must be visually distinct from one (adversarial review
            // finding, C4 #22328).
            deltaIntent="neutral"
          />
        )}
        {tile_statuses.gross_payroll === 'ready' ? (
          <StatTile
            kicker={t('dashboard.stat.gross_payroll')}
            value={format_money_compact(summary.gross_payroll, 'LKR', locale)}
            delta={
              summary.gross_payroll_delta_bp === null
                ? // No previous cycle to compare against -- render the
                  // "no comparison" copy rather than a misleading
                  // "0% vs last cycle" that reads as "unchanged"
                  // (issue #24350).
                  t('dashboard.stat.gross_payroll_no_previous_cycle')
                : t('dashboard.stat.gross_payroll_delta', {
                    delta: format_signed_basis_points(
                      summary.gross_payroll_delta_bp,
                      locale,
                    ),
                  })
            }
            // Derived from the REAL delta's sign -- a hardcoded 'positive'
            // was harmless against the old mock data (always a fixed +260
            // bp), but now that `gross_payroll_delta_bp` is real, a genuine
            // payroll DECREASE must render as negative, not a misleading
            // green (adversarial review finding, C4 #22328).
            deltaIntent={
              summary.gross_payroll_delta_bp === null
                ? 'neutral'
                : summary.gross_payroll_delta_bp > 0
                  ? 'positive'
                  : summary.gross_payroll_delta_bp < 0
                    ? 'negative'
                    : 'neutral'
            }
          />
        ) : (
          <StatTile
            kicker={t('dashboard.stat.gross_payroll')}
            value="—"
            delta={t('dashboard.tile_unavailable')}
            deltaIntent="neutral"
          />
        )}
        <ApprovalsTile
          status={pending_approvals_status}
          pending={pending_approvals}
          review_route={pending_approvals_route}
          locale={locale}
        />
        <ComplianceTile
          status={tile_statuses.compliance}
          alerts={summary.compliance_alerts}
          counts={compliance_counts}
          locale={locale}
        />
      </div>

      <div className="grid gap-4 items-start grid-cols-1 lg:grid-cols-[1.3fr_1fr]">
        <div className="flex flex-col gap-4">
          {tile_statuses.compliance === 'ready' ? (
            <StatutoryDeadlineCard
              deadline={summary.statutory_deadline}
              currency="LKR"
            />
          ) : (
            <GlassCard variant="strong" className="rounded-2xl p-5">
              <EmptyState
                title={t('dashboard.statutory.error_title')}
                description={t('dashboard.statutory.error_description')}
                action={
                  <Button variant="secondary" onClick={retry}>
                    {t('pay_run.retry')}
                  </Button>
                }
              />
            </GlassCard>
          )}
          <ActionNeededCard items={action_items} />
          {tile_statuses.activity === 'ready' ? (
            <ActivityFeedCard
              events={summary.activity}
              now={state.activity_now}
            />
          ) : (
            <GlassCard className="rounded-2xl p-5">
              <EmptyState
                title={t('dashboard.activity.error_title')}
                description={t('dashboard.activity.error_description')}
                action={
                  <Button variant="secondary" onClick={retry}>
                    {t('pay_run.retry')}
                  </Button>
                }
              />
            </GlassCard>
          )}
        </div>
        <div className="flex flex-col gap-4">
          {current_run && <CurrentRunCard run={current_run} />}
          <ExpiringDocumentsCard documents={summary.expiring_documents} />
        </div>
      </div>
    </>
  );
}

/**
 * Compliance-alerts KPI tile. Renders an unavailable state for non-ready
 * status, else the payroll-scoped overdue/due-soon count with a caption
 * driven by `deriveComplianceCaption`'s static discriminant. Own
 * `useTranslation('wp')` binding so every key is visible to the static
 * i18n-extract pass and the `no-dynamic-i18n-keys` ESLint rule (see the
 * migration pattern documented in
 * `packages/config-eslint/no-dynamic-i18n-keys.js`).
 */
function ComplianceTile({
  status,
  alerts,
  counts,
  locale,
}: {
  status: 'loading' | 'error' | 'ready';
  alerts: number;
  counts: ComplianceCounts;
  locale: string;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  if (status !== 'ready') {
    return (
      <StatTile
        kicker={t('dashboard.stat.compliance_alerts')}
        value="—"
        delta={t('dashboard.tile_unavailable')}
        deltaIntent="neutral"
      />
    );
  }
  const caption = deriveComplianceCaption(counts.overdue, counts.due_soon);
  let deltaText: string;
  switch (caption.kind) {
    case 'both':
      deltaText = t('dashboard.stat.compliance_caption_both', {
        overdue: caption.overdue,
        due_soon: caption.due_soon,
      });
      break;
    case 'overdue':
      deltaText = t('dashboard.stat.compliance_caption_overdue', {
        overdue: caption.overdue,
      });
      break;
    case 'due_soon':
      deltaText = t('dashboard.stat.compliance_caption_due_soon', {
        due_soon: caption.due_soon,
      });
      break;
    case 'none':
      deltaText = t('dashboard.stat.compliance_caption_none');
      break;
  }
  return (
    <StatTile
      kicker={t('dashboard.stat.compliance_alerts')}
      value={format_number(alerts, locale)}
      delta={deltaText}
      deltaIntent={alerts > 0 ? 'negative' : 'neutral'}
    />
  );
}

/**
 * Pending-approvals KPI tile. `expenses_gated` surfaces the "excludes expense
 * claims (plan)" caption; `error` renders the shared unavailable state. When
 * something is pending, a "Review now" link opens the queue holding it
 * (`review_route`) — the count alone gave the employer no way to find it.
 */
function ApprovalsTile({
  status,
  pending,
  review_route,
  locale,
}: {
  status: ApprovalsTileStatus;
  pending: number;
  review_route: Route | null;
  locale: string;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const review_action =
    review_route === null ? undefined : (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          navigate(review_route);
        }}
      >
        {t('dashboard.stat.review_pending')}
      </Button>
    );
  if (status === 'error') {
    return (
      <StatTile
        kicker={t('dashboard.stat.pending_approvals')}
        value="—"
        delta={t('dashboard.tile_unavailable')}
        deltaIntent="neutral"
      />
    );
  }
  if (status === 'expenses_gated') {
    return (
      <StatTile
        kicker={t('dashboard.stat.pending_approvals')}
        value={format_number(pending, locale)}
        // Expenses queue isn't included on the Lite plan (no
        // `expense_claims` feature); surface that explicitly instead of
        // silently under-counting (issue #24350 fixup).
        delta={t('dashboard.stat.pending_approvals_excludes_expenses')}
        deltaIntent="neutral"
        action={review_action}
      />
    );
  }
  return (
    <StatTile
      kicker={t('dashboard.stat.pending_approvals')}
      value={format_number(pending, locale)}
      // "Needs review" only makes sense when the count > 0 -- a
      // "0 · Needs review" is misleading (issue #24350).
      delta={pending > 0 ? t('dashboard.stat.needs_review') : undefined}
      deltaIntent={pending > 0 ? 'negative' : 'neutral'}
      action={review_action}
    />
  );
}
